import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SignalFilterStatus } from "@/enum/list.enum";

import { classNames } from "@/utils/classNames";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { OverviewTab } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/OverviewTab";
import { DeepResearchPanel } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/DeepResearchPanel";
import { SignalTimeline } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline";
import { hasValidDeepResearchLead } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/deepResearchLead";

import { CompanyHeader } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/CompanyHeader";

import { ContactLeadLocation } from "@/pages/Private/pages/Contacts/components/ContactLeadLocation";
import { LEAD_PANEL_STRIP_HEIGHT } from "@/pages/Private/pages/Contacts/components/LeadPanelCondensedHeader";
import {
	LeadContextTabs,
	LeadContextView,
} from "@/pages/Private/pages/Contacts/components/LeadContextTabs";
import { LeadPanelTabs } from "@/pages/Private/pages/Contacts/components/LeadPanelTabs";
import { LeadPanelActions } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/LeadPanelActions";
import { RecentSignalsBox } from "@/pages/Private/pages/Contacts/components/RecentSignalsBox";

import { useCondenseOnScroll } from "@/utils/useCondenseOnScroll";

import { useGetOrganizationProfilesQuery } from "@/pages/Private/redux/list/list.api";

import { LeadChatTab } from "@/pages/Private/pages/List/components/CampaignOverview/components/LeadChatTab";
import { useChatViewEntry } from "@/pages/Private/pages/Contacts/components/useChatViewEntry";

import { ListPerson } from "../../../schema/list";
import { toLeadLocationLists } from "../../../utils/toLeadLocationLists";
import { BlockedLeadBanner } from "./BlockedLeadBanner";
import { buildDetailViewTabs, DetailViewTab as Tabs } from "./detailViewTabs";
import { DetailViewCondensedHeader } from "./DetailViewCondensedHeader";
import { LeadPanelBackBar } from "./LeadPanelBackBar";
import { ListProfilesTab } from "./ListProfilesTab";
import { useProfileRetarget } from "./useProfileRetarget";
import { ListPersonHeader } from "./ListPersonHeader";
import { useDetailViewOutsideClose } from "./useDetailViewOutsideClose";
import { useLeadPanelReachOut } from "./useLeadPanelReachOut";
import { DetailViewOverviewSections } from "./DetailViewOverviewSections";
import { formatCurrentEmploymentRange, sortProspectCv } from "./prospectEmployment.utils";

interface DetailViewProps {
	listPerson?: ListPerson;
	setShowDetailView: (value: boolean) => void;
	signalFilterStatus?: SignalFilterStatus[];
	listId?: number;
	inline?: boolean;
	isHigherZ?: boolean;
	/**
	 * Hide the top-level "Lead Panel | Chat" toggle and only ever render the lead
	 * panel. Used on the Messages surfaces (Inbox / Outbox / ReadyToSend / Compose)
	 * which have their own chat affordances and don't want the per-lead Chat tab here.
	 */
	hideChatTab?: boolean;
	/**
	 * Open directly on the Chat view instead of the Lead Panel. Set when the panel is opened from the
	 * Outreach column's reach-out menu ("Write manually" / "Review" / "Reply"), which want the chat.
	 */
	openInChat?: boolean;
	/**
	 * Where "Reach out to {name}" should go on a host that has no per-lead Chat tab of its own. The
	 * Outbox passes its Chat tab; without it the button is not rendered at all rather than rendered
	 * dead (PREC-4107).
	 */
	onHostReachOut?: () => void;
}

export const DetailView: FunctionComponent<DetailViewProps> = ({
	listPerson: selectedListPerson,
	setShowDetailView,
	signalFilterStatus,
	listId,
	inline = false,
	isHigherZ = false,
	hideChatTab = false,
	openInChat = false,
	onHostReachOut,
}) => {
	// Clicking a colleague on the Profiles tab points this panel at them; `listPerson` below is that
	// colleague while one is picked, and the lead the table selected otherwise (PREC-4116).
	const {
		lead: listPerson,
		backToName,
		selectProfile,
		clear: clearRetarget,
	} = useProfileRetarget({ listPerson: selectedListPerson, listId });
	const ref = useRef<HTMLDivElement | null>(null);
	// The panel wrapper element as state, so it can be handed to the Chat tab as the
	// portal target for its dropdowns. They must render *inside* the outside-click
	// boundary (`ref`) — a `document.body` portal makes picking a sender/recipient
	// read as an outside click and close the panel (PREC-3627).
	const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
	const { t } = useTranslation();
	const { handleCopyClick, renderSnackbar } = useCopyToClipboard();
	const [showProspectImage, setShowProspectImage] = useState(true);
	const [searchParams, setSearchParams] = useSearchParams();
	const [companyLogoError, setCompanyLogoError] = useState(false);
	const [showSearchPerson, setShowSearchPerson] = useState(false);
	// Top-level Lead Panel | Chat toggle (v81 mockup `lead-ctx-tabs`). Opens on Chat when the reach-out
	// menu requested it (openInChat), otherwise on the Lead Panel.
	const {
		view: leadCtxView,
		autoOpenGenerateBar,
		openChatView,
		changeView: setLeadCtxView,
		resetView,
	} = useChatViewEntry(openInChat ? "chat" : "lead-panel");
	// Condense the identity block into a slim sticky strip once it scrolls out of view (v81 mockup).
	const { condensed, setCondensed, scrollRef, identityRef, anchorRef, onScroll } =
		useCondenseOnScroll();
	// Get tab from URL or default to OVERVIEW
	const currentTab = (searchParams.get("detailTab") as Tabs) || Tabs.OVERVIEW;

	const setCurrentTab = useCallback(
		(tab: Tabs) => {
			const newParams = new URLSearchParams(searchParams);

			newParams.set("detailTab", tab);
			setSearchParams(newParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	// Reset image visibility (and the condensed strip / scroll position) when listPerson changes
	useEffect(() => {
		setShowProspectImage(true);
		setCondensed(false);
		resetView(openInChat ? "chat" : "lead-panel");
		scrollRef.current?.scrollTo?.({ top: 0 });
	}, [listPerson?.id, openInChat, setCondensed, scrollRef, resetView]);

	useDetailViewOutsideClose({ ref, setShowDetailView, showSearchPerson, inline });

	const organization = useMemo(() => {
		return listPerson?.prospect?.organization || listPerson?.offer?.organization;
	}, [listPerson]);

	const prospect = useMemo(() => {
		return listPerson?.prospect;
	}, [listPerson]);

	// The other lists this lead belongs to (the current list is already excluded by the backend).
	const otherLeadLists = useMemo(
		() => toLeadLocationLists(listPerson?.otherLists),
		[listPerson?.otherLists]
	);

	const currentTrackerSignalResult = useMemo(() => {
		return listPerson?.trackerSignalResult;
	}, [listPerson]);

	const hasValidResearchLead = useMemo(() => {
		return hasValidDeepResearchLead(prospect);
	}, [prospect]);

	const offer = useMemo(() => {
		return listPerson?.offer;
	}, [listPerson]);

	const prospectCv = useMemo(() => sortProspectCv(prospect?.prospectEmployments), [prospect]);

	const currentCompanyUrl = useMemo(() => {
		let url = prospect?.organization?.websiteUrl || prospect?.organization?.primaryDomain;

		if (!url?.includes("http")) {
			url = "https://" + url;
		}

		return url;
	}, [prospect?.organization?.websiteUrl, prospect?.organization?.primaryDomain]);

	const currentEmploymentDateRange = useMemo(
		() => formatCurrentEmploymentRange(prospectCv),
		[prospectCv]
	);

	// Read deepResearchResponse from the Redux slice (updated by websocket)
	const { inbox, currentList } = useAppSelector(listSelector);

	const sliceDeepResearchResponse = useMemo(() => {
		if (!prospect?.id) {
			return undefined;
		}

		const listPersonInSlice = inbox.find((p) => p.prospect?.id === prospect.id);

		return listPersonInSlice?.prospect?.deepResearchResponse;
	}, [inbox, prospect?.id]);

	const deepResearchResponse = hasValidResearchLead
		? (sliceDeepResearchResponse ?? prospect?.deepResearchResponse)
		: undefined;

	const researchCount = useMemo(() => {
		const response = deepResearchResponse?.response;

		return response ? Object.keys(response).length : 0;
	}, [deepResearchResponse?.response]);

	const trackerOrganizationId = useMemo(() => {
		return (
			currentTrackerSignalResult?.trackerOrganizationId ||
			prospect?.trackerPersons?.[0]?.trackerOrganizationId ||
			0
		);
	}, [currentTrackerSignalResult, prospect?.trackerPersons]);

	const isDeepResearchActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);

	const { data: organizationProfiles, isLoading: profilesLoading } =
		useGetOrganizationProfilesQuery(listPerson?.id ?? 0, {
			skip: !listPerson?.id,
		});

	const profilesCount = organizationProfiles?.profiles?.length ?? 0;

	// Drive the Chat tab from the slice's live copy of this lead (kept fresh by the
	// copywriting websocket) when present, falling back to the prop row. The tab is
	// only offered when we actually have a listPerson id to send/generate against.
	const chatLead = useMemo(() => {
		if (!listPerson?.id) {
			return undefined;
		}

		return inbox.find((p) => p.id === listPerson.id) ?? listPerson;
	}, [inbox, listPerson]);

	// Reach-out goes to this panel's own Chat view where there is one (the Messages surfaces opt out
	// via `hideChatTab`), otherwise to whatever the host offers (the Outbox's Chat tab). A blacklisted
	// lead gets it behind the blacklist gate, and no Chat view until the user confirmed (PREC-2548);
	// an unsubscribed lead gets neither. A banner says why.
	const { isBlockedLead, showChat, reachOut, overrideModal } = useLeadPanelReachOut({
		lead: chatLead ?? listPerson,
		chatAvailable: !hideChatTab && !!chatLead,
		openChat: () => openChatView(false),
		onHostReachOut,
	});
	const isChatView = showChat && leadCtxView === "chat" && !!chatLead;

	const tabs = buildDetailViewTabs({ t, isDeepResearchActive, researchCount, profilesCount });

	const content = (
		<div className={classNames("flex h-full flex-col bg-white", inline ? "" : "shadow-lg")}>
			{backToName !== null && <LeadPanelBackBar name={backToName} onBack={clearRetarget} />}
			<div
				ref={(node) => {
					scrollRef.current = node;
				}}
				data-testid="detail-view-scroll"
				className={classNames(
					"min-h-0 flex-1 bg-white text-sm",
					// Chat view fills the panel with its own internal scroll (flex column, send bar
					// pinned to the bottom); the Lead Panel view scrolls the whole content instead.
					isChatView ? "flex flex-col overflow-hidden" : "overflow-y-auto"
				)}
				onScroll={onScroll}
			>
				{/* Slim sticky summary strip — fades in once the identity block scrolls away */}
				<DetailViewCondensedHeader
					offer={offer}
					organization={organization}
					prospect={prospect}
					visible={condensed}
				/>

				{/* Top-level Lead Panel | Chat toggle — only when a chat lead is available. The panel closes
				    on outside click, so no dedicated close button is rendered. */}
				{showChat && <LeadContextTabs view={leadCtxView} onChange={setLeadCtxView} />}

				{isChatView && chatLead ? (
					/* Fill the remaining panel height exactly (no viewport math) so the send bar sits
					   flush at the bottom; the chat log scrolls within. */
					<div className="min-h-0 flex-1">
						{/* Sending removes the lead from the inbox/draft list, so close the panel afterwards.
						   "Write manually" (openInChat) starts the Tell-AI bar collapsed. */}
						<LeadChatTab
							autoOpenGenerateBar={autoOpenGenerateBar}
							collapsedGenerateBar={openInChat}
							lead={chatLead}
							portalContainer={panelEl}
							// The portal target is the positioned panel wrapper, so the "Tell AI"
							// overlay can scope to the panel instead of dimming the whole page.
							scopeGeneratePanelToPortal
							onSent={() => setShowDetailView(false)}
						/>
					</div>
				) : (
					/* Main content area */
					<div className="pb-5 pt-2">
						{isBlockedLead && <BlockedLeadBanner listPerson={chatLead ?? listPerson} />}
						{/* Identity block — person + company + placement; scrolls away to reveal the strip.
						    `isolate` traps the Tooltip wrappers' z-100 (persona/category pills, CRM icons)
						    inside this block so they can't paint over the sticky condensed strip (z-20). */}
						<div ref={identityRef} className="isolate">
							{/* Person Header */}
							{prospect &&
								prospect.name &&
								!["SELECT", "Person not found", "Company not found"].includes(prospect.name) && (
									<ListPersonHeader
										currentCompanyUrl={currentCompanyUrl}
										nameRowRef={anchorRef}
										currentEmploymentDateRange={currentEmploymentDateRange}
										domain="list"
										employments={prospectCv}
										handleCopyClick={handleCopyClick}
										listPerson={listPerson}
										offer={offer}
										organization={organization}
										prospect={prospect}
										setShowProspectImage={setShowProspectImage}
										showProspectImage={showProspectImage}
									/>
								)}

							{listPerson &&
								prospect?.id &&
								prospect.name &&
								!["SELECT", "Person not found", "Company not found"].includes(prospect.name) && (
									<LeadPanelActions
										// Campaign actions stay on everywhere (PREC-4107): the Outbox is precisely
										// where a user watches a send being queued and wants to stop it. Only the
										// deep link is dropped on the Messages surfaces — the lead is already in the
										// view it would link to.
										campaignLocationLinkEnabled={!hideChatTab}
										crmProspects={prospect.crmProspects}
										firstName={prospect.firstName || (prospect.name?.split(" ")[0] ?? "")}
										listPerson={chatLead ?? listPerson}
										// The lead is on this list, so "Add to list" reads as already added.
										listPersons={[{ id: listPerson.id }]}
										portalContainer={panelEl}
										prospectId={prospect.id}
										prospectName={prospect.name ?? ""}
										reachOutEnabled={!!reachOut}
										trackerPersons={prospect.trackerPersons}
										onReachOut={() => reachOut?.()}
									/>
								)}

							<CompanyHeader
								companyLogoError={companyLogoError}
								organization={organization}
								setCompanyLogoError={setCompanyLogoError}
								trackerOrganizationName={organization?.name}
							/>
						</div>

						{/* Tabs */}
						<LeadPanelTabs
							activeTab={currentTab}
							stickyTopPx={condensed ? LEAD_PANEL_STRIP_HEIGHT : 0}
							tabs={tabs}
							onChange={setCurrentTab}
						/>

						<div className="isolate px-3 pb-4 pt-3">
							{currentTab === Tabs.OVERVIEW && (
								<>
									<OverviewTab
										belowSummary={
											// Other lists this lead is on — rendered directly below the Summary.
											listPerson?.otherLists && listPerson.otherLists.length > 0 ? (
												<ContactLeadLocation
													hideWhenEmpty
													lists={otherLeadLists.lists}
													listPersons={otherLeadLists.listPersons}
													prospectName={prospect?.name}
													titleKey="contacts.leadPanel.placement.otherListsTitle"
													onNavigate={() => setShowDetailView(false)}
												/>
											) : undefined
										}
										afterSummary={
											prospect?.id ? (
												<RecentSignalsBox
													companyName={organization?.name}
													conversations={
														chatLead?.prospect?.conversations ?? listPerson?.prospect?.conversations
													}
													firstName={prospect.firstName || (prospect.name?.split(" ")[0] ?? "")}
													isTracked={(prospect.trackerPersons?.length ?? 0) > 0}
													portalContainer={panelEl}
													prospectId={prospect.id}
													prospectName={prospect.name ?? ""}
													onSeeTimeline={() => setCurrentTab(Tabs.TIMELINE)}
												/>
											) : undefined
										}
										columns={listPerson?.columns || []}
										hasPersons={!!prospect}
										listPerson={listPerson}
										organization={organization}
										primaryPerson={prospect}
									/>

									<DetailViewOverviewSections offer={offer} />
								</>
							)}

							{currentTab === Tabs.RESEARCH && isDeepResearchActive && (
								<DeepResearchPanel
									deepResearchResponse={deepResearchResponse}
									// Inside the panel, like every other popover here: portaled to the body,
									// a click on a source counted as outside the panel (PREC-4168).
									portalContainer={panelEl}
									prospectId={hasValidResearchLead ? prospect?.id : undefined}
									// Only when this lead actually has a Chat view to jump to.
									onRequestSequence={showChat ? () => openChatView(true) : undefined}
								/>
							)}

							{currentTab === Tabs.TIMELINE && (
								<SignalTimeline
									prospectId={prospect?.id}
									signalFilterStatus={signalFilterStatus}
									trackerOrganizationId={trackerOrganizationId || undefined}
								/>
							)}

							{currentTab === Tabs.PROFILES && (
								<ListProfilesTab
									data={organizationProfiles}
									isLoading={profilesLoading}
									listId={listId}
									listName={currentList?.name}
									organization={organization}
									onModalOpenChange={setShowSearchPerson}
									onSelectProfile={selectProfile}
								/>
							)}
						</div>
					</div>
				)}
			</div>
			{renderSnackbar()}
			{overrideModal}
		</div>
	);

	if (inline) {
		return content;
	}

	return ReactDOM.createPortal(
		<div
			ref={(node) => {
				ref.current = node;
				setPanelEl(node);
			}}
			className={`absolute right-0 top-0 h-full w-[450px] transition-opacity ${
				isHigherZ ? `z-[1500]` : `z-[150]`
			}`}
		>
			<CollapseGrabber
				ariaLabel={t("basics.collapse")}
				collapsed={false}
				edge="left"
				onToggle={() => setShowDetailView(false)}
			/>
			{content}
		</div>,
		document.body
	);
};
